import React from "react";
import { View, TouchableOpacity, Text, ScrollView } from "react-native";
import { theme } from "../theme/theme";
import { filterChipShell, LISTING_BADGE_HEIGHT } from "../styles/listingBadgeStyles";
import Icon from "./Icon";

// Reusable Filter Chip Component
export const FilterChip = ({
  id,
  label,
  icon,
  isSelected,
  onPress,
  accessibilityLabel,
  accessibilityHint,
  style,
  textStyle,
  size = "compact", // 'compact' or 'large'
  showRemove = false,
}) => {
  const chipStyles =
    size === "compact" ? styles.compactFilterChip : styles.largeFilterChip;
  const selectedStyles =
    size === "compact" ? styles.selectedCompactChip : styles.selectedLargeChip;
  const textStyles =
    size === "compact" ? styles.compactChipText : styles.largeChipText;
  const selectedTextStyles =
    size === "compact"
      ? styles.selectedCompactChipText
      : styles.selectedLargeChipText;

  return (
    <TouchableOpacity
      style={[chipStyles, isSelected && selectedStyles, style]}
      onPress={() => onPress(id)}
      accessible={true}
      accessibilityLabel={
        accessibilityLabel || `${label}${isSelected ? ", selected" : ""}`
      }
      accessibilityHint={accessibilityHint}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
    >
      {icon && (
        <Icon
          name={icon}
          size={size === "compact" ? 14 : 16}
          color={
            isSelected ? theme.colors.onPrimary : theme.colors.onSurfaceVariant
          }
        />
      )}
      <Text style={[textStyles, isSelected && selectedTextStyles, textStyle]}>
        {label}
      </Text>
      {showRemove && isSelected && (
        <Icon
          name="close"
          size={12}
          color={theme.colors.onPrimary}
          style={{ marginLeft: 2 }}
        />
      )}
    </TouchableOpacity>
  );
};

// Filter Section Component for organizing filter groups
export const FilterSection = ({
  title,
  children,
  style,
  showScroll = false,
  horizontal = true,
}) => {
  const Container = showScroll ? ScrollView : View;
  const containerProps = showScroll
    ? {
        horizontal,
        showsHorizontalScrollIndicator: false,
        contentContainerStyle: horizontal
          ? styles.horizontalScrollContent
          : null,
      }
    : {};

  return (
    <View style={[styles.filterSection, style]}>
      {title && <Text style={styles.filterSectionTitle}>{title}</Text>}
      <Container {...containerProps}>{children}</Container>
    </View>
  );
};

// Active Filters Display Component
export const ActiveFiltersDisplay = ({
  activeFilters,
  onRemoveFilter,
  onClearAll,
  style,
}) => {
  if (activeFilters.length === 0) return null;

  return (
    <View style={[styles.activeFiltersContainer, style]}>
      <Text style={styles.activeFiltersTitle}>Active Filters</Text>
      <View style={styles.activeFiltersChips}>
        {activeFilters.map((filter) => (
          <TouchableOpacity
            key={filter.key}
            style={styles.activeFilterChip}
            onPress={() => onRemoveFilter(filter.key)}
            accessible={true}
            accessibilityLabel={`Remove ${filter.label} filter`}
            accessibilityHint="Remove this filter"
            accessibilityRole="button"
          >
            <Text style={styles.activeFilterText}>{filter.label}</Text>
            <Icon
              name="close"
              size={12}
              color={theme.colors.onSurfaceVariant}
            />
          </TouchableOpacity>
        ))}
        {activeFilters.length > 1 && (
          <TouchableOpacity
            style={styles.clearAllButton}
            onPress={onClearAll}
            accessible={true}
            accessibilityLabel="Clear all filters"
            accessibilityHint="Remove all active filters"
            accessibilityRole="button"
          >
            <Text style={styles.clearAllText}>Clear All</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

// Filter Toggle Button Component
export const FilterToggleButton = ({
  isExpanded,
  onPress,
  label = "Filters",
  accessibilityLabel,
  style,
}) => {
  return (
    <TouchableOpacity
      style={[styles.filterToggleButton, style]}
      onPress={onPress}
      accessible={true}
      accessibilityLabel={
        accessibilityLabel || `${isExpanded ? "Hide" : "Show"} ${label}`
      }
      accessibilityHint={`Toggle visibility of ${label.toLowerCase()}`}
      accessibilityRole="button"
      accessibilityState={{ expanded: isExpanded }}
    >
      <Icon name="filter-variant" size={16} color={theme.colors.primary} />
      <Text style={styles.filterToggleText}>{label}</Text>
      <Icon
        name={isExpanded ? "chevron-up" : "chevron-down"}
        size={16}
        color={theme.colors.onSurfaceVariant}
      />
    </TouchableOpacity>
  );
};

// Quick Filters Row Component
export const QuickFiltersRow = ({
  filters,
  selectedValues,
  onFilterPress,
  style,
}) => {
  return (
    <View style={[styles.quickFiltersRow, style]}>
      {filters.map((filter) => (
        <FilterChip
          key={filter.id}
          id={filter.id}
          label={filter.name}
          icon={filter.icon}
          isSelected={selectedValues[filter.key] === filter.id}
          onPress={(id) => onFilterPress(filter.key, id)}
          accessibilityHint={`Filter by ${filter.name.toLowerCase()}`}
          size="compact"
        />
      ))}
    </View>
  );
};

const styles = {
  // Filter Chip Styles
  compactFilterChip: {
    ...filterChipShell,
    backgroundColor: theme.colors.surfaceVariant,
    paddingHorizontal: theme.spacing.sm,
    marginRight: theme.spacing.xs,
    marginBottom: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.outline,
    gap: theme.spacing.xs,
  },
  selectedCompactChip: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  compactChipText: {
    fontSize: 14,
    fontWeight: "500",
    color: theme.colors.onSurfaceVariant,
    fontFamily: "Poppins",
  },
  selectedCompactChipText: {
    color: theme.colors.onPrimary,
  },
  largeFilterChip: {
    ...filterChipShell,
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    paddingHorizontal: theme.spacing.md,
    marginRight: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.outline,
    gap: theme.spacing.sm,
    height: LISTING_BADGE_HEIGHT,
    minHeight: LISTING_BADGE_HEIGHT,
    maxHeight: LISTING_BADGE_HEIGHT,
  },
  selectedLargeChip: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  largeChipText: {
    fontSize: 16,
    color: theme.colors.onSurface,
    fontFamily: "Poppins",
  },
  selectedLargeChipText: {
    color: theme.colors.onPrimary,
  },

  // Filter Section Styles
  filterSection: {
    marginBottom: theme.spacing.md,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: theme.colors.onSurface,
    fontFamily: "Poppins",
    marginBottom: theme.spacing.sm,
  },
  horizontalScrollContent: {
    paddingRight: theme.spacing.lg,
  },

  // Active Filters Styles
  activeFiltersContainer: {
    backgroundColor: theme.colors.surfaceVariant,
    borderRadius: 12,
    padding: theme.spacing.md,
    marginTop: theme.spacing.sm,
  },
  activeFiltersTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: theme.colors.onSurface,
    fontFamily: "Poppins",
    marginBottom: theme.spacing.sm,
  },
  activeFiltersChips: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing.sm,
  },
  activeFilterChip: {
    ...filterChipShell,
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    paddingHorizontal: theme.spacing.sm,
    gap: theme.spacing.xs,
  },
  activeFilterText: {
    fontSize: 14,
    fontWeight: "500",
    color: theme.colors.onSurface,
    fontFamily: "Poppins",
  },
  clearAllButton: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
  },
  clearAllText: {
    fontSize: 14,
    fontWeight: "600",
    color: theme.colors.primary,
    fontFamily: "Poppins",
    textDecorationLine: "underline",
  },

  // Filter Toggle Styles
  filterToggleButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.surfaceVariant,
    borderRadius: 20,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.outline,
    gap: theme.spacing.sm,
    alignSelf: "center",
  },
  filterToggleText: {
    fontSize: 14,
    fontWeight: "600",
    color: theme.colors.onSurfaceVariant,
    fontFamily: "Poppins",
  },

  // Quick Filters Row Styles
  quickFiltersRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: theme.spacing.xs,
    alignItems: "center",
  },
};
